import React, { useState, useEffect, useRef } from 'react';
import { customerAPI } from '../../services/api';
import './Customers.css';

const CustomerDetail = ({ customerId, onClose, onEdit, onDelete }) => {
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mapExpanded, setMapExpanded] = useState(false);
  const mapRef = useRef(null);
  const googleMapRef = useRef(null);
  const markerRef = useRef(null);

  useEffect(() => {
    fetchCustomer();
  }, [customerId]);

  // Initialize Google Map when customer coordinates are available
  useEffect(() => {
    if (customer && customer.latitude && customer.longitude && mapRef.current) {
      initMap();
    }

    return () => {
      // Cleanup marker on unmount or customer change
      if (markerRef.current) {
        markerRef.current.setMap(null);
        markerRef.current = null;
      }
    };
  }, [customer?.latitude, customer?.longitude]);

  const initMap = () => {
    // Check if Google Maps API is loaded
    if (!window.google || !window.google.maps) {
      console.warn('Google Maps API not loaded yet');
      return;
    }

    // If map already exists, just update position
    if (googleMapRef.current) {
      const newPosition = { lat: parseFloat(customer.latitude), lng: parseFloat(customer.longitude) };
      googleMapRef.current.panTo(newPosition);
      if (markerRef.current) {
        markerRef.current.setPosition(newPosition);
      } else {
        markerRef.current = new window.google.maps.Marker({
          position: newPosition,
          map: googleMapRef.current,
          title: customer.delivery_address || 'Customer Location'
        });
      }
      return;
    }

    const position = { lat: parseFloat(customer.latitude), lng: parseFloat(customer.longitude) };

    googleMapRef.current = new window.google.maps.Map(mapRef.current, {
      center: position,
      zoom: parseInt(customer.zoom_level) || 18,
      mapTypeId: 'satellite',
      mapTypeControl: true,
      zoomControl: true,
      scaleControl: true,
      streetViewControl: true,
      rotateControl: true,
      fullscreenControl: true
    });

    markerRef.current = new window.google.maps.Marker({
      position: position,
      map: googleMapRef.current,
      title: customer.delivery_address || 'Customer Location'
    });
  };

  const fetchCustomer = async () => {
    try {
      setLoading(true);
      const response = await customerAPI.getById(customerId);
      setCustomer(response);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load customer');
      console.error('Error fetching customer:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this customer?')) {
      return;
    }
    onDelete();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-content-wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Customer Details</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {loading ? (
            <div className="loading">Loading customer details...</div>
          ) : error ? (
            <div className="error-message">{error}</div>
          ) : customer ? (
            <div className="customer-detail-layout">
              <div className="customer-detail-main">
              <div className="detail-section">
                <h3>Personal Information</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Full Name</label>
                    <div className="detail-value">{customer.fullname}</div>
                  </div>
                  <div className="detail-item">
                    <label>Phone</label>
                    <div className="detail-value">{customer.phone}</div>
                  </div>
                  <div className="detail-item">
                    <label>Email</label>
                    <div className="detail-value">{customer.email || 'Not provided'}</div>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h3>Delivery Information</h3>
                <div className="detail-grid">
                  <div className="detail-item full-width">
                    <label>Delivery Address</label>
                    <div className="detail-value">
                      {customer.delivery_address || 'Not provided'}
                    </div>
                  </div>
                  <div className="detail-item">
                    <label>Trailer Access</label>
                    <div className="detail-value">
                      <span className={`badge ${customer.trailer_access ? 'badge-success' : 'badge-secondary'}`}>
                        {customer.trailer_access ? 'Yes' : 'No'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {customer.detail_values && customer.detail_values.length > 0 && (
                <div className="detail-section">
                  <h3>Additional Information</h3>
                  <div className="detail-grid">
                    {customer.detail_values.map((detailValue) => (
                      <div key={detailValue.id} className="detail-item">
                        <label>{detailValue.customer_detail?.name || 'Custom Field'}</label>
                        <div className="detail-value">
                          {renderCustomFieldValue(detailValue)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="detail-section">
                <h3>Record Information</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Created At</label>
                    <div className="detail-value">
                      {new Date(customer.created_at).toLocaleString()}
                    </div>
                  </div>
                  <div className="detail-item">
                    <label>Last Updated</label>
                    <div className="detail-value">
                      {new Date(customer.updated_at).toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>
              </div>

              {customer.delivery_address && customer.latitude && customer.longitude && (
                <div className={`customer-detail-sidebar ${mapExpanded ? 'sidebar-expanded' : ''}`}>
                  <div className="detail-section">
                    <h3>Location Map</h3>
                    <div className={`map-container ${mapExpanded ? 'map-expanded' : ''}`}>
                      <div
                        ref={mapRef}
                        className="interactive-map"
                        style={{ width: '100%', height: mapExpanded ? '500px' : '350px' }}
                      />
                      <div className="map-controls">
                        <button
                          className="btn btn-map-expand"
                          onClick={() => setMapExpanded(!mapExpanded)}
                          title={mapExpanded ? 'Shrink map' : 'Expand map'}
                        >
                          <span className="map-icon">{mapExpanded ? '⊖' : '⊕'}</span>
                          {mapExpanded ? ' Shrink' : ' Expand'}
                        </button>
                      </div>
                      <div className="map-actions">
                        <button
                          className="btn btn-map-action"
                          onClick={() => {
                            const url = `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${customer.latitude},${customer.longitude}`;
                            window.open(url, '_blank', 'noopener,noreferrer');
                          }}
                          title="Open in Google Street View"
                        >
                          <span className="map-icon">👁</span>
                          Street View
                        </button>
                        <button
                          className="btn btn-map-action"
                          onClick={() => {
                            const url = `https://www.google.com/maps/search/?api=1&query=${customer.latitude},${customer.longitude}`;
                            window.open(url, '_blank', 'noopener,noreferrer');
                          }}
                          title="Open in Google Maps"
                        >
                          <span className="map-icon">↗</span>
                          Google Maps
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="error-message">Customer not found</div>
          )}
        </div>

        {customer && (
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose}>
              Close
            </button>
            <button className="btn btn-warning" onClick={onEdit}>
              Edit
            </button>
            <button className="btn btn-danger" onClick={handleDelete}>
              Delete
            </button>
          </div>
        )}
      </div>
    </div>
  );

  function renderCustomFieldValue(detailValue) {
    const fieldType = detailValue.customer_detail?.field_type;
    const value = detailValue.value;

    if (!value) {
      return 'Not provided';
    }

    switch (fieldType) {
      case 'checkbox':
        return (
          <span className={`badge ${value === 'true' ? 'badge-success' : 'badge-secondary'}`}>
            {value === 'true' ? 'Yes' : 'No'}
          </span>
        );

      case 'date':
        try {
          return new Date(value).toLocaleDateString();
        } catch {
          return value;
        }

      case 'textarea':
        return <div style={{ whiteSpace: 'pre-wrap' }}>{value}</div>;

      default:
        return value;
    }
  }
};

export default CustomerDetail;
